/**
 * NeedsYouTab — the pinned pane's queue-first surface (story pinned-pane).
 *
 * A HOST, not a component: every pixel comes from the shared tree
 * (`@product-app/common/workflows/**`, design Constraint 20) and every row of
 * data comes from a server read. This file wires three things together and
 * owns no rendering logic of its own:
 *
 *   1. `useWaitingQueue` — the server read, invalidated by user-scoped push,
 *      with a poll as the correctness floor.
 *   2. `WaitingQueue`    — the shared two-group list.
 *   3. `useOpenToItem`   — the open-to-item address (AC-9), whose result is
 *      ACTED ON here: an addressed row is focused, anything else falls back to
 *      the queue with a plain statement.
 *
 * Opening a row routes into the EXISTING `InstanceDetailDrawer` entry point
 * that the Launch tab's In-flight list uses. No second decision surface is built
 * here (design Constraint 17).
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  QUEUE_EMPTY_COPY,
  QUEUE_NOTHING_NEEDED_COPY,
  WaitingQueue,
} from "@product-app/common/workflows/components/WaitingQueue";
import { ReadyBriefsQueue } from "@product-app/common/workflows/components/ReadyBriefsQueue";
import { useWaitingQueue } from "@product-app/common/workflows/hooks/useWaitingQueue";
import { isInitialLoading } from "@product-app/common/workflows/initialLoading";
import { automatedKpiWindowCopy } from "@product-app/common/workflows/copy";
import type { ReadyBriefItem } from "../types/workflow";
import { orchestratorApi } from "../services/orchestratorApi";
import { useOpenToItem } from "../hooks/useOpenToItem";
import { useVisiblePoll } from "../hooks/useVisiblePoll";
import { BRIEF_GONE_NOTICE, resolveReadyBriefForOpen } from "../features/readyBriefDetail";
import {
  detectTargetMailbox,
  type SharedMailboxDetection,
} from "../features/sharedMailbox/detectTargetMailbox";
import { openOraMailboxes, openOraMonitor } from "../oraWeb";
import { openMessageInOutlook } from "../outlook/openMessage";
import { HopAffordance } from "./HopAffordance";

export interface NeedsYouTabProps {
  /**
   * Open one queue row. The host resolves it into the existing
   * `InstanceDetailDrawer` live-mode path — the same one the Active tab uses.
   */
  onOpenItem: (instanceId: string, workflowId: string) => void;
  /**
   * Raised on every successful read so the tab badge can show the SERVER's
   * count. Never a length computed here.
   */
  onCountChange?: (count: number) => void;
  /** Page size forwarded to the server. */
  limit?: number;
  /** Disables the hook's timers and sockets under test. */
  disabled?: boolean;
  /** Injected only so tests and the render probe can pin "now". */
  now?: Date;
  /** SMTP of this Outlook session's mailbox (ready-brief filter fallback). */
  currentMailboxAddress?: string | null;
  /** Unused: the running row is status-only; oraMonitor hops out. */
  onOpenRunning?: () => void;
  /**
   * Open the pre-launch form for a prepared brief that is not the current email.
   * Receives the FULL brief: the tab reads it on click (the list is cards); on a
   * failed read it receives the card; for a brief launched or ignored since
   * (404) it is not called at all and the tab shows a notice instead (Ruling R20).
   */
  onOpenBrief?: (item: ReadyBriefItem) => void;
  /** RFC 5322 id of the email currently selected — omitted from the ready-to-start list. */
  currentMessageId?: string | null;
  /**
   * Bumped by the host (`InstanceDetailDrawer`'s `onHitlResponded`) right
   * after the AM answers a HITL prompt, so the row they just acted on drops
   * out of "Needs you" without waiting for the push/poll floor. Still just an
   * earlier REFETCH, never a locally-computed removal — the server's rows
   * remain the only truth (design Constraint 19).
   */
  refreshSignal?: number;
}

/**
 * How long a failed-click notice stays up, in ms.
 *
 * 15s: long enough to read a two-line explanation without hunting for it, short
 * enough that it is gone before the AM comes back to the pane for something
 * else. Named rather than inlined so the timer and its test cannot drift.
 */
export const CLICK_NOTICE_MS = 15_000;

/** Ready-to-start polls on the pane's old cadence — it is CARDS now (compact:
 *  no prefill, confidence or provenance), a few KB rather than ~50. */
export const READY_BRIEFS_POLL_MS = 30_000;
/** Waiting-queue cadence while the pane is hidden. */
export const HIDDEN_QUEUE_POLL_MS = 5 * 60_000;

/** "N mailboxes monitored" moves when someone enrols or links a mailbox —
 *  minutes, not seconds (Joachim, 2026-10-06). */
export const MAILBOX_SUMMARY_POLL_MS = 5 * 60_000;

function briefsForMailbox(
  rows: ReadyBriefItem[],
  mailbox: string | null,
): ReadyBriefItem[] {
  if (!mailbox) return rows;
  const key = mailbox.toLowerCase();
  return rows.filter(
    (row) =>
      !row.source_mailbox || row.source_mailbox.toLowerCase() === key,
  );
}

export function NeedsYouTab({
  onOpenItem,
  onCountChange,
  limit = 50,
  disabled = false,
  now,
  currentMailboxAddress,
  onOpenRunning: _onOpenRunning,
  onOpenBrief,
  currentMessageId,
  refreshSignal,
}: NeedsYouTabProps): React.ReactElement {
  const [detection, setDetection] = useState<SharedMailboxDetection | "pending">(
    "pending",
  );

  useEffect(() => {
    let cancelled = false;
    void detectTargetMailbox().then((detected) => {
      if (cancelled) return;
      setDetection(detected);
    });
    return () => {
      cancelled = true;
    };
  }, [currentMailboxAddress]);

  // Personal Outlook: do NOT send the profile SMTP. HSIP mail vs UPN
  // (joachim.landes@… vs jlandes@hsip.com) is the same person; scoping to
  // whichever Outlook returned hides the run. Shared sessions still
  // exact-match the detected mailbox (AC 3).
  const scopedMailbox =
    detection !== "pending" && detection.kind === "shared"
      ? detection.targetMailbox
      : null;

  const mailboxScopeReady = detection !== "pending";

  const listWaiting = useCallback(
    (pageSize?: number) => orchestratorApi.listWaiting(pageSize, scopedMailbox),
    [scopedMailbox],
  );
  const getUserPushChannel = useCallback(
    () => orchestratorApi.getUserPushChannel(),
    [],
  );

  const queue = useWaitingQueue({
    listWaiting,
    getUserPushChannel,
    limit,
    disabled: disabled || !mailboxScopeReady,
    // A hidden pane has nobody reading it; becoming visible refetches at once.
    // Passed here, not changed in the shared hook, so ora keeps its 30 s.
    hiddenIntervalMs: HIDDEN_QUEUE_POLL_MS,
  });

  // Skips the initial render (ref seeded with the first value seen) so
  // mounting the tab never double-fetches on top of the hook's own
  // fetch-on-mount; only a genuine bump from the host triggers this.
  const seenRefreshSignalRef = useRef(refreshSignal);
  useEffect(() => {
    if (refreshSignal === undefined) return;
    if (seenRefreshSignalRef.current === refreshSignal) return;
    seenRefreshSignalRef.current = refreshSignal;
    void queue.refetch();
  }, [refreshSignal, queue.refetch]);

  // The badge is the SERVER's count, forwarded unchanged.
  const { count, lastSuccessAt } = queue;
  useEffect(() => {
    if (lastSuccessAt === null) return;
    onCountChange?.(count);
  }, [count, lastSuccessAt, onCountChange]);

  // ── Open-to-item (AC-9) ───────────────────────────────────────────────────
  // MOUNTED HERE and its result acted on below. A hook tested only in
  // isolation is a correct-but-unwired deliverable that passes every automated
  // check while nothing happens in the pane.
  const queueInstanceIds = useMemo(
    () => queue.items.map((i) => i.instance_id),
    [queue.items],
  );
  const getStatus = useCallback(
    (instanceId: string) => orchestratorApi.getStatus(instanceId),
    [],
  );
  const openToItem = useOpenToItem({
    queueInstanceIds,
    queueLoaded: queue.lastSuccessAt !== null,
    getStatus,
    disabled,
  });

  // Focus IS the selection for a keyboard-operable list: the addressed row is
  // the one the AM's next keystroke acts on. Applied once per resolved address
  // (guarded by ref) so a poll refresh cannot yank focus back later.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [selectedInstanceId, setSelectedInstanceId] = useState<string | null>(null);
  const focusedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (openToItem.outcome !== "in_queue" || !openToItem.instanceId) return;
    if (focusedForRef.current === openToItem.instanceId) return;
    const row = containerRef.current?.querySelector<HTMLElement>(
      `[data-instance-id="${CSS?.escape ? CSS.escape(openToItem.instanceId) : openToItem.instanceId}"]`,
    );
    if (!row) return; // the row has not rendered yet — retry on the next pass
    focusedForRef.current = openToItem.instanceId;
    setSelectedInstanceId(openToItem.instanceId);
    try {
      row.focus();
    } catch {
      /* focus is best-effort; the selection attribute still stands */
    }
  }, [openToItem.outcome, openToItem.instanceId, queue.items]);

  const [watchedCount, setWatchedCount] = useState<number | null>(null);
  const [readyBriefs, setReadyBriefs] = useState<ReadyBriefItem[]>([]);
  // Ready briefs follow the same stale-while-revalidate rule as the queue
  // (`isInitialLoading`): the placeholder shows only before the first
  // successful read; later refreshes swap data in without touching the screen.
  const [briefsLastSuccessAt, setBriefsLastSuccessAt] = useState<number | null>(null);
  const [briefsLastError, setBriefsLastError] = useState<unknown>(null);
  /** Why the last click did nothing, or null. Rendered, not logged.
   *
   *  Carries BOTH Open Email refusals and ora-hop failures. A hop throws when
   *  the ora origin was never configured at build time, and that throw used to
   *  land in a bare `void` — no window, no message, a button that read as
   *  broken. Same class of failure as a silent Open Email, so it gets the same
   *  surface rather than a second one.
   *
   *  An OBJECT rather than the bare message string, and that is load-bearing:
   *  the auto-dismiss effect below keys on this value, so two failing clicks
   *  carrying the SAME text must still be two distinct values. A string would
   *  compare equal, the effect would not re-run, and the second notice would
   *  inherit the first click's already-running timer — vanishing early, by
   *  however long the AM took to click again. */
  const [clickNotice, setClickNotice] = useState<{
    message: string;
  } | null>(null);

  // Auto-dismiss. The notice explains a click that produced nothing, so it has
  // done its job the moment it has been read — leaving it pinned would make the
  // pane look broken long after the AM moved on, and there is no action to take
  // on it.
  //
  // The cleanup is what makes repeat failures behave: a new notice tears down
  // the previous timer before starting its own, so each one gets a full window
  // rather than inheriting whatever was left of the last. Unmounting mid-window
  // cancels rather than setting state on a gone component.
  useEffect(() => {
    if (!clickNotice) return;
    const timer = window.setTimeout(() => setClickNotice(null), CLICK_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [clickNotice]);

  /**
   * Run an ora hop, rendering the reason when it cannot open.
   *
   * `void open()` was the second half of the dead-hop bug: `openOraWebPath`
   * throws when the ora origin was never configured at build time, the
   * rejection went unhandled, and the click produced NOTHING — no window, no
   * message, no way to tell a broken build from a broken button. The throw
   * already carries the fix in its text, so the only thing missing was a place
   * to put it.
   */
  const hop = useCallback((open: () => Promise<void>) => {
    open().catch((err: unknown) => {
      setClickNotice({
        message:
          err instanceof Error && err.message
            ? err.message
            : "Could not open ora.",
      });
    });
  }, []);

  // Ready-to-start CARDS. A card's click reads the full brief (openBrief); the
  // list no longer carries every brief's prefill every 30 s.
  const loadBriefs = useCallback(
    (isCurrent: () => boolean) => {
      void orchestratorApi
        .listReadyBriefs(limit, { compact: true })
        .then((r) => {
          if (!isCurrent()) return;
          setReadyBriefs(briefsForMailbox(r.items ?? [], scopedMailbox));
          setBriefsLastSuccessAt(Date.now());
          setBriefsLastError(null);
        })
        .catch((err: unknown) => {
          if (!isCurrent()) return;
          setReadyBriefs([]);
          setBriefsLastError(err ?? new Error("listReadyBriefs failed"));
        });
    },
    [limit, scopedMailbox],
  );
  useVisiblePoll(loadBriefs, READY_BRIEFS_POLL_MS, !disabled);

  // A card is a card (compact list): its click reads the brief ONCE and hands the
  // host the full brief, exactly as the list used to carry it. One read at a
  // time, so a double-click cannot open the form twice.
  const openingBriefRef = useRef(false);
  // Pane-owned feedback while that read is in flight (cleared on every outcome).
  const [openingBrief, setOpeningBrief] = useState(false);
  // The read can outlive the tab (App mounts it only on the Needs-you pane; the
  // read may hang ~60 s). A late result must not call the host — it would replace
  // a form the AM opened meanwhile and start a background prefill — nor set state.
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const onOpenBriefRef = useRef(onOpenBrief);
  onOpenBriefRef.current = onOpenBrief;
  const openBrief = useCallback(
    async (item: ReadyBriefItem) => {
      if (!onOpenBrief || openingBriefRef.current) return;
      openingBriefRef.current = true;
      setOpeningBrief(true);
      try {
        const opened = await resolveReadyBriefForOpen(item, (mid, mailbox) =>
          orchestratorApi.getReadyBrief(mid, mailbox),
        );
        if (!aliveRef.current) return;
        if (opened.outcome === "gone") {
          // Rulings R20/R26/R27: launched or ignored since the last read. Opening
          // the form would start a background workflow_prefill run for an email
          // that is already handled, so say so, drop the card now (as Ignore
          // does) and let the refresh confirm.
          setClickNotice({ message: BRIEF_GONE_NOTICE });
          const mid = item.internet_message_id;
          setReadyBriefs((prev) => prev.filter((row) => row.internet_message_id !== mid));
          loadBriefs(() => true);
          return;
        }
        onOpenBriefRef.current?.(opened.brief);
        // The read failed for another reason: the form opened on the card.
        // Refresh so a card that has since gone does not keep offering itself.
        if (opened.outcome === "fallback") loadBriefs(() => true);
      } finally {
        openingBriefRef.current = false;
        if (aliveRef.current) setOpeningBrief(false);
      }
    },
    [onOpenBrief, loadBriefs],
  );

  // "N mailboxes monitored": ONE server-side number (watched-mailboxes/summary,
  // same rule the pane used to apply here). It used to download every watched
  // row — ~28 fields each, plus product SQL enrichment — every 30 s to count them.
  const loadMailboxCount = useCallback((isCurrent: () => boolean) => {
    void orchestratorApi
      .getWatchedMailboxSummary()
      .then((n) => {
        if (isCurrent()) setWatchedCount(n);
      })
      .catch(() => {
        /* count stays whatever we last knew */
      });
  }, []);
  useVisiblePoll(loadMailboxCount, MAILBOX_SUMMARY_POLL_MS, !disabled);

  // The SERVER's running_count, from the same waiting read as the rows
  // (ListWaiting always sends it). A second listInstances poll every 30 s used to
  // stand in for it. Null — no read yet — renders the em-dash, never a guessed 0.
  const runningCount = queue.runningCount;
  // `null` until a read succeeds — kept null, not collapsed to 0, so the row
  // renders the same em-dash the Running row already uses. Collapsing it meant
  // the pane stated "0" beside its own "Couldn't refresh — no successful update
  // yet" banner: an assertion made from a read that never happened, which is
  // the untruth the queue's own stale-labelling rule exists to prevent.
  const triagedToday = queue.triagedToday;
  const emptyCopy =
    (runningCount ?? 0) === 0
      ? QUEUE_NOTHING_NEEDED_COPY
      : QUEUE_EMPTY_COPY;

  return (
    <div
      ref={containerRef}
      className="flex min-h-full flex-col gap-3 p-4"
      data-testid="needs-you-tab"
      data-selected-instance-id={selectedInstanceId ?? undefined}
    >
      {/* The fallback statement for an address that is unknown, already
          answered, or another AM's. All three say only what this AM is
          entitled to know; two of them say exactly the same thing. */}
      {openToItem.message && (
        <p
          role="status"
          data-testid="open-to-item-message"
          className="rounded-[10px] border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2 text-xs text-primary-gray-light-600"
        >
          {openToItem.message}
        </p>
      )}

      <WaitingQueue
        items={queue.items}
        // Never `queue.loading`: that is true on every refetch and would swap
        // "Needs you · 0" for "Checking…" each poll/push. Placeholder only
        // before the first successful read (shared rule, see initialLoading).
        loading={isInitialLoading({
          ready: mailboxScopeReady,
          lastSuccessAt: queue.lastSuccessAt,
          lastError: queue.lastError,
        })}
        isStale={queue.isStale}
        staleReason={queue.staleReason}
        lastSuccessAt={queue.lastSuccessAt}
        now={now}
        emptyCopy={emptyCopy}
        onOpenItem={(instanceId) => {
          const row = queue.items.find((i) => i.instance_id === instanceId);
          if (!row) return;
          setSelectedInstanceId(instanceId);
          onOpenItem(instanceId, row.workflow_id);
        }}
      />

      {/* Open Email used to fail in silence: the helper swallowed everything
          into a console.warn, and displayMessageForm reports nothing when its
          id resolves to no message. The AM clicked and got no window and no
          reason. Now the refusal is rendered where they clicked. */}
      {clickNotice ? (
        <div
          role="alert"
          className="mb-2 rounded-md border border-primary-warning-300 bg-primary-warning-50 px-3 py-[6px] text-xs text-primary-warning-700"
          data-testid="pane-click-notice"
        >
          {clickNotice.message}
        </div>
      ) : null}

      {openingBrief ? (
        <p
          role="status"
          data-testid="ready-brief-opening"
          className="text-xs text-primary-gray-light-500"
        >
          Opening brief…
        </p>
      ) : null}

      <ReadyBriefsQueue
        items={readyBriefs}
        loading={
          !disabled &&
          isInitialLoading({
            lastSuccessAt: briefsLastSuccessAt,
            lastError: briefsLastError,
          })
        }
        currentMessageId={currentMessageId}
        onOpenBrief={(item) => void openBrief(item)}
        onOpenInOutlook={(item) => {
          const result = openMessageInOutlook(item.graph_message_id ?? "");
          // Cleared on success so a later good click removes a stale notice.
          setClickNotice(result.ok ? null : { message: result.message });
        }}
        onIgnore={(item) => {
          const mid = item.internet_message_id;
          setReadyBriefs((prev) =>
            prev.filter((row) => row.internet_message_id !== mid),
          );
          void orchestratorApi.ignoreReadyBrief(mid).catch(() => {
            loadBriefs(() => true);
          });
        }}
      />

      <div
        className="mt-1 flex flex-shrink-0 items-center gap-2.5 rounded-lg border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-3"
        data-testid="pinned-running-row"
      >
        <span className="flex-1 text-left text-xs leading-[18px] text-primary-gray-light-500">
          Running · {runningCount ?? "—"} — nothing needed
        </span>
        <HopAffordance
          testId="pinned-ora-monitor"
          onClick={() => hop(openOraMonitor)}
        >
          oraMonitor
        </HopAffordance>
      </div>

      <div className="flex flex-shrink-0 flex-col gap-[7px] border-t border-primary-gray-light-200 pt-2.5">
        <div
          className="flex items-baseline gap-2.5"
          data-testid="pinned-triage-row"
        >
          <span className="flex-1 text-xs text-primary-gray-light-400">
            {/* ora's own words for this figure, from the shared copy module —
                it is completed automated RUNS over a rolling 24 hours, which
                "triaged today" got wrong in both halves. */}
            {triagedToday ?? "—"} {automatedKpiWindowCopy("1d")}
          </span>
          <HopAffordance
            testId="pinned-triage-hop"
            onClick={() => hop(openOraMonitor)}
          >
            numbers
          </HopAffordance>
        </div>
        <div
          className="flex items-baseline gap-2.5"
          data-testid="pinned-mailbox-row"
        >
          <span className="flex-1 text-xs text-primary-gray-light-400">
            {watchedCount === null
              ? "Mailbox monitoring"
              : `${watchedCount} mailbox${watchedCount === 1 ? "" : "es"} monitored`}
          </span>
          <HopAffordance
            testId="pinned-mailbox-hop"
            onClick={() => hop(openOraMailboxes)}
          >
            set up
          </HopAffordance>
        </div>
      </div>

      <p className="flex-shrink-0 text-2xs leading-[17px] text-primary-gray-light-400">
        Only decisions live here. No settings, no stats, no monitor — those are
        one hop away in ora.
      </p>
    </div>
  );
}

export default NeedsYouTab;
